import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Searchable single-select. Lightweight — wraps a button-trigger that
 * opens a popover with an inline search field and a virtualized-ish list
 * (we just slice to MAX_RESULTS so giant lists like IANA timezones don't
 * blow up the DOM).
 *
 * Props:
 *   value, onChange — the standard controlled-input contract
 *   options — pass {value, label}; falls back to using value as label
 *   placeholder — shown when nothing is selected
 */
const MAX_RESULTS = 200;

export interface ComboboxOption {
  value: string;
  label?: string;
  hint?: string;
}

export function Combobox({
  value, onChange, options, placeholder = "Select…", className, disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  // Click-outside.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const norm = (s: string) => s.toLowerCase().replace(/_/g, " ");
  const filtered = (() => {
    if (!q.trim()) return options.slice(0, MAX_RESULTS);
    const needle = norm(q);
    const matches: ComboboxOption[] = [];
    for (const o of options) {
      const hay = norm(o.label ?? o.value) + " " + norm(o.value) + " " + norm(o.hint ?? "");
      if (hay.includes(needle)) {
        matches.push(o);
        if (matches.length >= MAX_RESULTS) break;
      }
    }
    return matches;
  })();

  const current = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg border bg-zinc-900/50 px-3 h-9 text-sm text-left transition-colors",
          "border-zinc-800 hover:bg-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <span className={cn("flex-1 truncate", value ? "text-zinc-100" : "text-zinc-500")}>
          {current?.label ?? value ?? placeholder}
        </span>
        <ChevronDown className={cn("h-3.5 w-3.5 text-zinc-500 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-shade/40 animate-fade-in">
          <div className="border-b border-zinc-800 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="w-full rounded-md bg-zinc-900 pl-8 pr-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30"
              />
            </div>
          </div>
          <ul className="max-h-72 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <li className="px-2 py-3 text-xs text-zinc-500">No matches.</li>
            ) : filtered.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm hover:bg-zinc-900",
                    o.value === value && "bg-zinc-900 text-zinc-100",
                  )}
                >
                  <span className="truncate text-zinc-200">{o.label ?? o.value}</span>
                  {o.hint && <span className="ml-2 shrink-0 text-[10px] text-zinc-500">{o.hint}</span>}
                </button>
              </li>
            ))}
            {!q.trim() && options.length > MAX_RESULTS && (
              <li className="px-2 py-2 text-[10px] text-zinc-600">Showing {MAX_RESULTS} of {options.length} — search to narrow.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
